import { z } from 'zod';
import { isPermissionCode } from './permissions.ts';

/** Idiomas suportados pela interface. */
export const LOCALES = ['pt-BR', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

const trimmed = (max: number) => z.string().trim().min(1).max(max);
export const currencyCode = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Código ISO 4217 com 3 letras maiúsculas');
export const uuid = z.uuid();
export const timezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Fuso horário inválido (ex.: America/Sao_Paulo)');
export const localizedText = z.object({ 'pt-BR': trimmed(200), en: trimmed(200) });

/** Código curto em maiúsculas, usado em relatórios e integrações (ex.: FINANCEIRO, VENDAS). */
export const shortCode = z
  .string()
  .trim()
  .regex(/^[A-Z0-9][A-Z0-9_-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, - ou _');

export const userInput = z.object({
  email: z.email().trim().toLowerCase().max(254),
  displayName: trimmed(200),
  locale: z.enum(LOCALES).default('pt-BR'),
  timezone: timezone.default('America/Sao_Paulo'),
  isActive: z.boolean().default(true),
});
export type UserInput = z.infer<typeof userInput>;

/** Login com e-mail e senha (ADR 0109). A senha não é aparada: espaços contam. */
export const loginInput = z.object({
  // Apara antes de conferir: o celular costuma pôr um espaço no fim do e-mail.
  email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginInput>;

/** Tamanho mínimo da senha nova (ADR 0127; a provisória do servidor tem 16). */
export const PASSWORD_MIN = 8;

export const passwordChangeInput = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z
      .string()
      .min(PASSWORD_MIN, `A senha precisa de pelo menos ${PASSWORD_MIN} caracteres`)
      .max(200),
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    message: 'A senha nova precisa ser diferente da atual',
  });
export type PasswordChangeInput = z.infer<typeof passwordChangeInput>;

/** "Esqueci a senha": pede o link por e-mail (ADR 0113). O idioma escolhe o texto do e-mail. */
export const passwordForgotInput = z.object({
  email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
  locale: z.enum(['pt-BR', 'en']).default('pt-BR'),
});
export type PasswordForgotInput = z.infer<typeof passwordForgotInput>;

/** Nova senha a partir do link do e-mail. */
export const passwordResetInput = z.object({
  token: z.string().min(20).max(200),
  newPassword: z
    .string()
    .min(PASSWORD_MIN, `A senha precisa de pelo menos ${PASSWORD_MIN} caracteres`)
    .max(200),
});
export type PasswordResetInput = z.infer<typeof passwordResetInput>;

export const roleInput = z.object({
  code: shortCode,
  name: localizedText,
  description: z.object({ 'pt-BR': z.string().max(500), en: z.string().max(500) }).optional(),
  permissions: z
    .array(z.string().refine(isPermissionCode, 'Permissão desconhecida'))
    .max(500)
    .transform((list) => [...new Set(list)].sort()),
  isActive: z.boolean().default(true),
});
export type RoleInput = z.infer<typeof roleInput>;

/** Concessão de um perfil a uma pessoa (vale no sistema inteiro). */
export const roleAssignmentInput = z.object({ userId: uuid, roleId: uuid });
export type RoleAssignmentInput = z.infer<typeof roleAssignmentInput>;

/** Valor decimal como texto, para nunca passar dinheiro ou câmbio por ponto flutuante. */
export const decimalString = (intDigits: number, scale: number) =>
  z
    .string()
    .trim()
    .regex(
      new RegExp(`^\\d{1,${intDigits}}(\\.\\d{1,${scale}})?$`),
      `Número com até ${scale} casas decimais, usando ponto`,
    );

export const pageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
